import React from 'react';
import { Eye, Edit2, Trash2, ImageIcon, Book, Calendar, Users, Tag } from 'lucide-react';

const CardView = ({
  items: rawItems,
  activeTab,
  onView,
  onEdit,
  onDelete,
  getThumbnailUrl,
  truncateText,
  isReadOnly,
}) => {
  const items = Array.isArray(rawItems) ? rawItems : [];
  const renderCourseCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
      <div className="aspect-video w-full bg-gray-100 relative">
        {item.thumbnail ? (
          <img 
            src={getThumbnailUrl(item.thumbnail)}
            alt={item.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `
                <div class="absolute inset-0 flex items-center justify-center">
                  <ImageIcon class="w-12 h-12 text-gray-400" />
                </div>
              `;
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-gray-400" />
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-lg mb-2">{item.title}</h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {item.description || 'No description available'}
        </p>
        
        {/* Tags Section */}
        {item.tags && item.tags.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {item.tags.slice(0, 3).map(tag => (
                <span
                  key={tag._id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {tag.title}
                </span>
              ))}
              {item.tags.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  +{item.tags.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center text-sm text-[#1086a4]">
            <Book className="w-4 h-4 mr-1" />
            <span className="font-medium">{item.lessons?.length || 0} Lessons</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onView(item)}
              className="p-1.5 text-[#1086a4] hover:text-[#054581] transition-colors rounded-full hover:bg-gray-100"
            >
              <Eye size={18} />
            </button>
            {!isReadOnly && (
              <>
                <button
                  onClick={() => onEdit(item)}
                  className="p-1.5 text-[#1086a4] hover:text-[#054581] transition-colors rounded-full hover:bg-gray-100"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => onDelete(item)}
                  className="p-1.5 text-red-500 hover:text-red-700 transition-colors rounded-full hover:bg-gray-100"
                >
                  <Trash2 size={18} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderEventCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
      <div className="aspect-video w-full bg-gray-100 relative">
        {item.thumbnail ? (
          <img 
            src={getThumbnailUrl(item.thumbnail)}
            alt={item.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `
                <div class="absolute inset-0 flex items-center justify-center">
                  <ImageIcon class="w-12 h-12 text-gray-400" />
                </div>
              `;
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-gray-400" />
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-lg mb-2">{item.title}</h3>
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <div>
            {item.type === 'event' && item.date && (
              <div className="flex items-center gap-1 text-[#1086a4]">
                <Calendar className="w-4 h-4" />
                <span>{new Date(item.date).toLocaleDateString('en-US', { 
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}</span>
              </div>
            )}
            <div className={`flex items-center gap-1 text-[#1086a4] ${item.type === 'event' && item.date ? 'mt-1' : ''}`}>
              <Users className="w-4 h-4" />
              <span>{item.program?.title || 'No Program'}</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => onView(item)}
            className="p-1.5 text-[#1086a4] hover:text-[#054581] transition-colors rounded-full hover:bg-gray-100"
          >
            <Eye size={18} />
          </button>
          {!isReadOnly && (
            <>
              <button
                onClick={() => onEdit(item)}
                className="p-1.5 text-[#1086a4] hover:text-[#054581] transition-colors rounded-full hover:bg-gray-100"
              >
                <Edit2 size={18} />
              </button>
              <button
                onClick={() => onDelete(item)}
                className="p-1.5 text-red-500 hover:text-red-700 transition-colors rounded-full hover:bg-gray-100"
              >
                <Trash2 size={18} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {items.map(item => {
        switch (activeTab) {
          case 'Courses':
            return renderCourseCard(item);
          case 'Events & Information':
            return renderEventCard(item);
          default:
            return null;
        }
      })}
    </div>
  );
};

export default CardView; 